/**
 * Convert the kitchen-layout modal's config into a flat list of
 * appliances that need a placement, each with footprint dimensions.
 *
 * Footprints are wall-aligned: `width` is along the wall, `depth` is
 * into the room, `height` is vertical. Heights are mostly informational
 * for v1 (placement is XZ-only), but we keep them so future features
 * — wall-oven Y-positioning, fridge clearance — have a single source
 * of truth.
 */

const {
  INCHES_TO_METERS,
  KITCHEN_SINK_BASE_WIDTH,
  DISHWASHER_WIDTH,
} = require('../constants');
const {
  KITCHEN_DIMENSION_POLICY,
} = require('../../dimensions/dimensionPolicy');

const P = KITCHEN_DIMENSION_POLICY;

/**
 * Standard appliance footprints. Derived from common spec sheets.
 *
 * The width/depth/height dimensions that the post-processing snap rules
 * also use (stove, fridge, dishwasher depth) read the shared dimension
 * policy's `design` tiers (shared-lib/dimensions/dimensionPolicy.js) so the
 * layout and post-processing paths share one source of truth. `design.standard`
 * is the standard-tier size; `design.large` the large-tier size. (The policy's
 * separate `scan` lists govern how scanned dims are snapped — not used here.)
 * Non-policy dims (fridge/sink/wall-oven heights, sink depth) stay inline.
 */
const APPLIANCE_FOOTPRINTS = {
  sink: {
    // Standard 36" sink base — shared with the island sink so a real
    // (32-35") catalog sink fits the model-selection cap. See
    // KITCHEN_SINK_BASE_WIDTH in constants.js.
    width: KITCHEN_SINK_BASE_WIDTH,
    depth: 24 * INCHES_TO_METERS,
    height: 34.5 * INCHES_TO_METERS,
  },
  dishwasher: {
    width: DISHWASHER_WIDTH,
    depth: P.dishwasherDepth.design.standard,
    height: 34.5 * INCHES_TO_METERS,
  },
  stoveStandard: {
    width: P.stoveWidth.design.standard,
    depth: P.stoveDepth.design.standard,
    height: P.stoveHeight.design.standard,
  },
  // A DROP-IN COOKTOP is not a small range: it is a deck cut into the
  // countertop with a base cabinet underneath it. Its own dimension tiers
  // already exist (the scan path needs them — see `classifyCooktops`), so the
  // run reads those rather than borrowing the range's 36"-tall body, which is
  // what made a cooktop reserve a range-shaped hole with nothing under it.
  cooktop: {
    width: P.cooktopWidth.design.standard,
    depth: P.cooktopDepth.design.standard,
    height: P.cooktopHeight.design.standard,
  },
  stoveLarge: {
    width: P.stoveWidth.design.large,
    depth: P.stoveDepth.design.standard,
    height: P.stoveHeight.design.standard,
  },
  fridgeStandard: {
    width: P.fridgeWidth.design.standard,
    depth: P.fridgeDepth.design.standard,
    height: 70 * INCHES_TO_METERS,
  },
  fridgeLarge: {
    width: P.fridgeWidth.design.large,
    depth: P.fridgeDepth.design.standard,
    height: 70 * INCHES_TO_METERS,
  },
  wallOven: {
    width: 30 * INCHES_TO_METERS,
    depth: 24 * INCHES_TO_METERS,
    height: 28 * INCHES_TO_METERS,
  },
};

/**
 * @param {Object} config - Modal config: { sinkCount, oven, stove, fridge }
 * @param {{ sinkWidthsIn?: number[] }} [opts] the sink bases (inches) the
 *   cabinet line sells, up to 36"; none = the standard 36" only
 * @returns {Array<{
 *   id: string, type: string, width: number, depth: number,
 *   height: number, requiresAdjacent?: string, widths?: number[]
 * }>} `widths` (the sink's): the widths a spot may take, widest first;
 *   `width` is the standard one.
 */
function applianceRequirements(config, { sinkWidthsIn = null } = {}) {
  if (!config) return [];
  const appliances = [];

  // The sink bases a spot may take, widest first: the cabinet line's own
  // sizes up to the standard 36", handed in by the caller
  // (`cabinet-runs/sinkBaseWidths.kitchenSinkBaseWidthsIn` — not required
  // here: cabinet-runs/bays reads APPLIANCE_FOOTPRINTS from this module). A
  // spot takes 36" wherever it fits and steps down only where it does not
  // (user ruling 2026-11-03, as a generated island already did on 2026-10-21).
  const sinkWidths = (sinkWidthsIn || [])
    .map((w) => w * INCHES_TO_METERS)
    .sort((a, b) => b - a);
  for (let i = 0; i < (config.sinkCount || 1); i++) {
    appliances.push({
      id: `sink_${i + 1}`,
      type: 'sink',
      ...APPLIANCE_FOOTPRINTS.sink,
      ...(sinkWidths.length ? { widths: sinkWidths } : {}),
    });
  }

  appliances.push({
    id: 'range',
    type: 'range',
    ...(config.stove === 'large'
      ? APPLIANCE_FOOTPRINTS.stoveLarge
      : APPLIANCE_FOOTPRINTS.stoveStandard),
  });

  appliances.push({
    id: 'fridge',
    type: 'fridge',
    ...(config.fridge === 'large'
      ? APPLIANCE_FOOTPRINTS.fridgeLarge
      : APPLIANCE_FOOTPRINTS.fridgeStandard),
  });

  if (config.oven === 'wall') {
    appliances.push({
      id: 'wallOven',
      type: 'wallOven',
      ...APPLIANCE_FOOTPRINTS.wallOven,
    });
  }

  appliances.push({
    id: 'dishwasher',
    type: 'dishwasher',
    ...APPLIANCE_FOOTPRINTS.dishwasher,
    requiresAdjacent: 'sink',
  });

  return appliances;
}

module.exports = { applianceRequirements, APPLIANCE_FOOTPRINTS };
